/**
 * WebSocket manager for a single conversation.
 *
 * Responsibilities:
 *   - Open a WebSocket with the auth token.
 *   - Auto-reconnect with exponential backoff (1s, 2s, 4s, … capped at 30s).
 *   - Heartbeat: send `ping` every 25s.
 *   - Deliver typed events to a single listener.
 *   - Support manual close and explicit reconnect.
 */

import { getToken } from './api';
import { normalizeWsMessage } from './adapters';

export type ServerEvent =
  | { type: 'message'; data: unknown }
  | { type: 'presence'; data: { userId: string; online: boolean; lastSeen?: string | null } }
  | { type: 'typing'; data: { userId: string; isTyping: boolean } }
  | { type: 'poll_update'; data: unknown }
  | { type: 'pong' }
  | { type: 'ready'; data?: unknown }
  | { type: string; data?: unknown };

export type ClientCommand =
  | { type: 'send'; message: { content: string; replyToId?: string } }
  | { type: 'typing'; isTyping: boolean }
  | { type: 'ping' };

export type WsStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface ConversationSocketOptions {
  url: string; // full ws:// URL including token query
  onEvent: (e: ServerEvent) => void;
  onStatusChange?: (s: WsStatus) => void;
  // Called when the WS upgrade is rejected with 401 (token expired or
  // revoked). The client will not auto-reconnect after this fires — the
  // consumer is expected to log the user out and redirect to /login.
  onAuthError?: () => void;
}

// How many abnormal-closure events in a row we tolerate before deciding
// the server is rejecting our auth (vs a transient network blip). Cloudflare
// surfaces an HTTP 401 on a WS upgrade as close code 1006, so we can't tell
// the two apart from a single close — but if every attempt produces 1006,
// the token is bad.
const MAX_ABNORMAL_CLOSES_BEFORE_AUTH_ERROR = 2;

// Backoff schedule for reconnection. Capped at 30s.
const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];

export class ConversationSocket {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private heartbeatTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private explicitlyClosed = false;
  private status: WsStatus = 'idle';
  private currentUrl: string;
  // Counter for abnormal closures since the last successful open. When
  // this exceeds the threshold we conclude the server is rejecting our
  // auth (because Cloudflare surfaces 401 on a WS upgrade as 1006).
  private abnormalCloses = 0;

  constructor(private readonly opts: ConversationSocketOptions) {
    this.currentUrl = opts.url;
  }

  connect(): void {
    this.explicitlyClosed = false;
    this.openSocket(this.currentUrl);
  }

  reconnect(newUrl?: string): void {
    if (newUrl) this.currentUrl = newUrl;
    this.attempt = 0;
    this.abnormalCloses = 0;
    this.teardownSocket();
    this.openSocket(this.currentUrl);
  }

  updateUrl(newUrl: string): void {
    this.currentUrl = newUrl;
  }

  send(cmd: ClientCommand): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(cmd));
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }

  close(): void {
    this.explicitlyClosed = true;
    this.teardownSocket();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.setStatus('closed');
  }

  getStatus(): WsStatus {
    return this.status;
  }

  /** Build a WS URL for the given conversation id using the current token. */
  static buildUrl(baseHttp: string, conversationId: string): string {
    const token = getToken() ?? '';
    const base = baseHttp.replace(/\/$/, '');
    const wsBase = base.startsWith('https://')
      ? base.replace('https://', 'wss://')
      : base.startsWith('http://')
        ? base.replace('http://', 'ws://')
        : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;
    return `${wsBase}/wschat/${encodeURIComponent(conversationId)}?token=${encodeURIComponent(token)}`;
  }

  private openSocket(url: string): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.setStatus('connecting');
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = socket;

    socket.addEventListener('open', () => {
      this.attempt = 0;
      this.abnormalCloses = 0;
      this.setStatus('open');
      this.startHeartbeat();
    });

    socket.addEventListener('message', (ev) => {
      let parsed: ServerEvent | null = null;
      try {
        parsed = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as ServerEvent;
      } catch {
        return;
      }
      if (parsed) this.opts.onEvent(normalizeWsMessage(parsed) as ServerEvent);
    });

    socket.addEventListener('close', (ev) => {
      this.stopHeartbeat();
      this.ws = null;
      if (this.explicitlyClosed) {
        this.setStatus('closed');
        return;
      }
      // Explicit policy-violation close codes the chat-room DO uses for
      // an auth failure.
      if (ev && (ev.code === 1008 || ev.code === 4401 || ev.code === 4403)) {
        this.setStatus('error');
        try {
          this.opts.onAuthError?.();
        } catch {
          /* swallow */
        }
        return;
      }
      // Abnormal closures (1006) on a fresh upgrade that never opened
      // are almost certainly an HTTP 401 from the WS upgrade — the
      // browser never sees a Close frame. Track them so we can stop
      // hammering the server after a couple of failed attempts and
      // instead surface an auth error to the consumer.
      if (ev && ev.code === 1006 && this.attempt === 0) {
        this.abnormalCloses += 1;
        if (this.abnormalCloses >= MAX_ABNORMAL_CLOSES_BEFORE_AUTH_ERROR) {
          this.setStatus('error');
          try {
            this.opts.onAuthError?.();
          } catch {
            /* swallow */
          }
          return;
        }
      } else {
        this.abnormalCloses = 0;
      }
      this.setStatus('closed');
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      this.setStatus('error');
      // 'close' will fire next and handle reconnect.
    });
  }

  private teardownSocket(): void {
    this.stopHeartbeat();
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.explicitlyClosed) return;
    const idx = Math.min(this.attempt, BACKOFF_MS.length - 1);
    const delay = BACKOFF_MS[idx];
    this.attempt += 1;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket(this.currentUrl);
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      this.send({ type: 'ping' });
    }, 25_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private setStatus(s: WsStatus): void {
    if (this.status === s) return;
    this.status = s;
    this.opts.onStatusChange?.(s);
  }
}
